import { useMemo, useState, useCallback, Fragment, useRef, use, useLayoutEffect, memo } from 'react';
import gsap from 'gsap/all';
import dayjs from 'dayjs';

import { fetchSectionData } from '@/lib/get-data';
import withUIContext from '@context/consumerHOC/UIConsumer';
import withLanguageContext from '@/context/consumerHOC/LanguageConsumer';

//* Components
import Image from '@components/global/Image';
import Text from '@/components/global/Text';
import Filter from '@components/global/Filter';
import CustomLink from '@components/global/CustomLink';
import HTMLContainer from '@components/global/HTMLContainer';
import AnimationWrap from '@components/global/AnimationWrap';

//* Styles
import PortfolioListStyle from './style';

const PortfolioList = memo(({ data: outsideData, serviceType, portfolioFull, isFilter = false, winWidth, selectedLang }) => {
	const data = outsideData || use(fetchSectionData({ name: 'getPortfolioShortList', lang: selectedLang, params: { serviceType } }));

	if (!data) return;

	const itemsLength = Object.keys(data.items).length;

	const initialPortfolioItems = Array.from({ length: itemsLength }, (_, index) => {
		return {
			[`sec${++index}`]: false,
		};
	});

	//! States
	const [portfolioItems, setPortfolioItems] = useState(initialPortfolioItems);
	const [hoverKey, setHoverKey] = useState();
	const [filterSelect, setFilterSelect] = useState('all');

	//! Refs
	const ref = useRef([]);

	//! Handle Active Section Animation
	const Anim = useCallback(
		(x) => {
			if (JSON.stringify(x) !== JSON.stringify(portfolioItems)) {
				setPortfolioItems(x);
			}
		},
		[portfolioItems]
	);

	const changeFilterType = (currentTarget) => {
		setFilterSelect(currentTarget);
	};

	let counter = 0;

	const filtersItem = useMemo(() => {
		let arr = [];

		if (data) {
			const itemsArr = Object.values(data.items);
			for (let i = 0; i < itemsArr.length; i++) {
				const item = itemsArr[i];
				arr.push(item.fields.workType);
			}
		}

		return arr;
	}, [data]);

	const animate = useCallback((el) => {
		if (!el) return;
		gsap.killTweensOf(el);
		gsap.to(el, { duration: 0.3, opacity: 1, overwrite: 'auto' });
	}, []);

	//! List Single Item
	const singleItem = useCallback(
		(item, i) => {
			const filterArr = filtersItem.filter((i, k) => {
				return i === item.fields.workType;
			});

			return (
				<div
					key={i}
					onMouseLeave={setHoverKey}
					onMouseEnter={() => setHoverKey(i)}
					className={`portfolioItem case-${filterArr.length}`}>
					<CustomLink
						className={`crLight`}
						url={`/portfolio/${item.fields.slug}`}>
						<Fragment>
							<div
								ref={(el) => (ref.current[i] = el)}
								className={`image_video_cont ${hoverKey === i ? 'image_active' : ''}`}>
								{hoverKey === i &&
									(counter === 2 || counter === 5 ? (
										<video
											ref={(videoRef) => animate(videoRef)}
											src={item.fields.shortVideo.fields.file.url}
											loop
											autoPlay
											muted
										/>
									) : (
										<video
											ref={(videoRef) => animate(videoRef)}
											src={item.fields.shortVideoLandscape.fields.file.url}
											loop
											autoPlay
											muted
										/>
									))}

								{counter === 2 || counter === 5 ? (
									<Image
										src={item.fields.smallImage.fields.file.url}
										alt={item.fields.smallImage.fields.title}
										className={`p-image image_video`}
									/>
								) : (
									<Image
										src={item.fields.smallImageLandscape.fields.file.url}
										alt={item.fields.smallImageLandscape.fields.title}
										className={`p-image image_video`}
									/>
								)}
							</div>

							<Text
								tag={'h3'}
								className={`h6 font-poppins font-medium title`}>
								{item.fields.title}
							</Text>

							<Text className={`p-m font-poppins font-regular description`}>{item.fields.workType}</Text>

							<Text className={`p-m font-poppins font-italic createdDate`}>{dayjs(item.fields.createdAt).format('YYYY')}</Text>
						</Fragment>
					</CustomLink>
				</div>
			);
		},
		[hoverKey, portfolioItems, counter, filterSelect, filtersItem]
	);

	//! Items
	const items = useMemo(() => {
		let arr = [];

		if (data && portfolioItems) {
			const itemsArr = Object.values(data.items),
				count = itemsArr.length;
			for (let i = 0; i < count; i++) {
				if (serviceType === itemsArr[i].fields.serviceType || (!serviceType && (filterSelect === 'all' || filterSelect === itemsArr[i].fields.workType))) {
					const item = itemsArr[i];

					counter < 6 ? counter++ : (counter = 1);

					arr.push(singleItem(item, i, portfolioItems[`sec${[i + 1]}`]));
				}
			}
		}
		return arr;
	}, [data, hoverKey, portfolioItems, counter, filterSelect, serviceType]);

	useLayoutEffect(() => {
		const nodes = (ref.current || []).filter(Boolean);
		if (!nodes.length) return;

		const w = typeof window !== 'undefined' ? window.innerWidth : 0;

		nodes.forEach((el, i) => {
			const delay = w >= 768 && i % 2 !== 0 ? 0.1 : 0;
			const visible = Boolean(portfolioItems?.[`sec${i + 1}`] || i === 0 || i === 1);

			if (visible) {
				gsap.set(el, { opacity: 1 });
				gsap.to(el, { duration: 0.4, rotationY: 0, delay, overwrite: 'auto' });
			} else {
				gsap.set(el, { opacity: 0.1, rotationY: -70 });
			}
		});

		return () => {
			gsap.killTweensOf(nodes);
			ref.current = [];
		};
	}, [portfolioItems, selectedLang, filterSelect]);

	return (
		<AnimationWrap
			state={Anim}
			portfolioType>
			<PortfolioListStyle className={`section-item ${!isFilter ? 'not-padding' : ''}`}>
				{isFilter && (
					<Filter
						data={filtersItem}
						parentCallback={changeFilterType}
					/>
				)}

				<HTMLContainer>
					<div className={`portfolioList ${portfolioFull ? 'full' : ''}`}>{items}</div>
				</HTMLContainer>
			</PortfolioListStyle>
		</AnimationWrap>
	);
});

export default withUIContext(withLanguageContext(PortfolioList, ['selectedLang']), ['winWidth']);
